import React, { useState, useEffect, useRef } from 'react';
import { useField, useFormikContext } from 'formik';
import { useDebounce } from 'use-debounce';
import { IconSearch } from '@tabler/icons-react';
import { npiService } from '../../services/npiService';
import loadingGif from '../../assets/images/loader.gif';

const NPILookup = ({
  name,
  label = 'NPI Number',
  required = false,
  className = '',
  disabled = false,
  children,
}) => {
  const [field, meta] = useField(name);
  const { setFieldValue, setFieldTouched, validateForm } = useFormikContext();

  const [searchMode, setSearchMode] = useState('number');
  const [searchValue, setSearchValue] = useState(field.value);
  // Track if this is an initial field value (pre-populated)
  const [isInitialValue, setIsInitialValue] = useState(field.value.length > 0);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedFromSuggestions, setSelectedFromSuggestions] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const [debouncedSearchValue] = useDebounce(searchValue, 300);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const performSearch = async () => {
      if (selectedFromSuggestions || !debouncedSearchValue.trim()) {
        return;
      }

      if (
        (searchMode === 'number' &&
          (debouncedSearchValue.length !== 10 ||
            !/^\d+$/.test(debouncedSearchValue))) ||
        (searchMode === 'name' && debouncedSearchValue.length < 3)
      ) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
      }

      if (!isInitialValue) {
        setIsLoading(true);
        setShowDropdown(true);
      } else {
        setIsInitialValue(false);
      }

      try {
        let data;
        if (searchMode === 'number') {
          data = await npiService.searchByNumber(debouncedSearchValue);
        } else {
          const [firstName, ...lastNameParts] = debouncedSearchValue.split(' ');
          const lastName = lastNameParts.join(' ');
          data = await npiService.searchByName(firstName, lastName);
        }

        if (data.result_count > 0) {
          const formattedSuggestions = data.results.map(
            npiService.formatNPIProvider,
          );
          setSuggestions(formattedSuggestions);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        console.error('Error fetching NPI suggestions:', err);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchValue]);

  const handleInputChange = (e) => {
    setFieldValue('selectedNpi', null);
    const value = e.target.value;
    if (value.length <= 10) {
      setFieldTouched(name, true);
      setFieldValue(name, value);
    }
    if (searchMode === 'number') {
      const cleanValue = value.replace(/\D/g, '').slice(0, 10);
      setSearchValue(cleanValue);
      setSelectedFromSuggestions(false);
    } else {
      setSearchValue(value);
      setSelectedFromSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSelectedFromSuggestions(true);
    setSearchMode('number');
    setSearchValue(suggestion.npi);
    setFieldValue(name, suggestion.npi);
    setFieldTouched(name, true);
    setFieldValue('selectedNpi', suggestion).then(() => {
      validateForm();
    });
    setShowDropdown(false);
    setSuggestions([suggestion]);
  };

  const toggleSearchMode = () => {
    setSearchMode(searchMode === 'number' ? 'name' : 'number');
    setSearchValue('');
    setFieldValue(name, '');
    setFieldValue('selectedNpi', '').then(() => {
      validateForm();
    });
    setSuggestions([]);
    setShowDropdown(false);
    setSelectedFromSuggestions(false);
  };

  return (
    <div className={`mb-3 ${className}`} ref={dropdownRef}>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label htmlFor={name} className="pb-1 small fw-semibold">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
        <button
          type="button"
          onClick={toggleSearchMode}
          className="btn border-0 btn-sm p-0 text-primary"
          disabled={disabled}
        >
          {searchMode === 'number'
            ? 'Search by name instead'
            : 'Enter NPI number'}
        </button>
      </div>

      <div className="position-relative">
        {searchMode === 'number' ? (
          <div className="position-relative">
            <input
              id={name}
              autoComplete="off"
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setFieldTouched(name, true)}
              className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
              placeholder="Enter 10-digit NPI number"
              disabled={disabled}
            />
          </div>
        ) : (
          <div className="input-group">
            <span className="input-group-text">
              <IconSearch size={18} />
            </span>
            <input
              id={name}
              autoComplete="off"
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setFieldTouched(name, true)}
              className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
              placeholder="Search by provider name"
              disabled={disabled}
            />
          </div>
        )}

        {/* Dropdown suggestions */}
        {showDropdown && (
          <div
            className="position-absolute w-100 bg-white overflow-auto mt-1"
            style={{
              maxHeight: '240px',
              zIndex: 1000,
              display: showDropdown ? 'block' : 'none',
            }}
          >
            {isLoading ? (
              <div className="dropdown-item m-0 p-0">
                <span className="text-primary">
                  <img
                    style={{
                      maxHeight: '80px',
                      height: '60px',
                      display: 'block',
                      margin: '0 auto',
                    }}
                    src={loadingGif}
                    alt="Loading..."
                    className="loader-gif"
                  />
                </span>
              </div>
            ) : suggestions.length > 0 ? (
              suggestions.map((suggestion) => (
                <button
                  key={suggestion.npi}
                  type="button"
                  className="w-100 text-start px-3 py-2 border-0 bg-white hover-bg-light"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div>
                    {suggestion.prefix && `${suggestion.prefix} `}
                    {suggestion.firstName} {suggestion.middleName}{' '}
                    {suggestion.lastName}
                    {suggestion.credential && `, ${suggestion.credential}`}
                  </div>
                  <div className="small text-muted">NPI: {suggestion.npi}</div>
                </button>
              ))
            ) : debouncedSearchValue && !isLoading ? (
              <div className="p-3 text-center text-muted">
                {searchMode === 'number' && searchValue.length < 10
                  ? 'Please enter all 10 digits'
                  : 'No results found'}
              </div>
            ) : null}
          </div>
        )}

        {meta.touched && (
          <>
            {searchMode === 'name' ? (
              children ? (
                <div>{children}</div>
              ) : meta.error ? (
                <div className="invalid-feedback d-block">{meta.error}</div>
              ) : null
            ) : meta.error ? (
              <div className="invalid-feedback d-block">{meta.error}</div>
            ) : children ? (
              <div>{children}</div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default NPILookup;
